import { useEffect, useRef, useState } from 'react';
import {
  steadyCountConfirmed,
  steadyCountMoving,
  steadyCountStart,
  steadyCountStep,
  STEADY_COUNT_QUIET_MS,
  STEADY_COUNT_TICK_MS,
  STEADY_COUNT_WINDOW_MS,
  type SteadyCount,
} from './steady-count';

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A documents-list tab's number, held steady around the person's confirmations (steady-count.ts).
 * `scope` starts it over (another job or batch). `confirmations` grows with each confirm or skip;
 * `busy` is true while one is still landing; `explained` while a pull-back has just been reported.
 * Re-renders itself while the number is on its way.
 */
export function useSteadyCount(
  count: number,
  scope: string,
  confirmations: number,
  busy: boolean,
  explained = false,
): SteadyCount {
  const held = useRef<{ scope: string; confirmations: number; state: SteadyCount } | null>(null);
  const [, setFrame] = useState(0);
  const now = Date.now();
  if (!held.current || held.current.scope !== scope)
    held.current = { scope, confirmations, state: steadyCountStart(count, now) };
  if (held.current.confirmations !== confirmations) {
    held.current.confirmations = confirmations;
    held.current.state = steadyCountConfirmed(held.current.state, now);
  }
  held.current.state = steadyCountStep(held.current.state, count, now, {
    busy,
    explained,
    instant: reducedMotion(),
  });
  const state = held.current.state;
  useEffect(() => {
    if (!steadyCountMoving(state)) return;
    // A tick while the number steps; otherwise one look when the quiet (a held rise) or the
    // confirmation's window ends. Never a tight loop over the whole workspace.
    const now = Date.now();
    const wait =
      state.shown !== state.target
        ? STEADY_COUNT_TICK_MS
        : Math.max(
            500,
            state.latest > state.shown
              ? STEADY_COUNT_QUIET_MS - (now - state.latestAt) + 20
              : state.confirmedAt + STEADY_COUNT_WINDOW_MS - now + 20,
          );
    const timer = window.setTimeout(() => setFrame((frame) => frame + 1), wait);
    return () => window.clearTimeout(timer);
  });
  return state;
}
